import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, "")}${path}`
  }
  return path
}

export interface PlotPlanDto {
  plotPlanId: string
  locationId: string
  unitId?: string | null
  workAreaCode: string
  workAreaName: string
  unitCode?: string | null
  unitName?: string | null
  name: string
  description?: string | null
  latestVersionId?: string | null
  latestVersion?: number | null
  backgroundPath?: string | null
  isActive: boolean
}

export type PlotPlanStatusFilter = "all" | "active" | "inactive"

export interface PlotPlanUpsertRequest {
  tenantId: string
  workArea: string
  name: string
  description?: string
  isActive: boolean
}

export interface WorkAreaOption {
  locationId: string
  code: string
  name: string
}

export interface UnitOption {
  unitId: string
  locationId: string
  code: string
  name: string
}

export interface PlotPlanVersionUploadRequest {
  tenantId: string
  plotPlanId: string
  versionName?: string
  remark?: string
  file: File
}

export interface PlotPlanVersionDto {
  plotPlanVersionId: string
  version?: number | null
  backgroundPath?: string | null
  isActive?: boolean | null
}

export interface PagedResultDto<T> {
  items: T[]
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
}

interface SearchPlotPlanParams {
  tenantId: string
  search?: string
  workArea?: string
  unit?: string
  status?: PlotPlanStatusFilter
  page?: number
  pageSize?: number
}

export async function searchPlotPlans({
  tenantId,
  search,
  workArea,
  unit,
  status = "all",
  page = 1,
  pageSize = 20,
}: SearchPlotPlanParams): Promise<PagedResultDto<PlotPlanDto>> {
  const response = await axios.get<any>(buildUrl("/api/v1/location-plot-plans"), {
    params: {
      tenantId,
      search,
      workArea,
      unit,
      status,
      page,
      pageSize,
    },
  })
  const data = response.data
  if (Array.isArray(data)) {
    return {
      items: data,
      pageNumber: 1,
      pageSize: data.length,
      totalItems: data.length,
      totalPages: 1,
    }
  }
  return data as PagedResultDto<PlotPlanDto>
}

export async function createPlotPlan(payload: PlotPlanUpsertRequest): Promise<PlotPlanDto> {
  const response = await axios.post<PlotPlanDto>(buildUrl("/api/v1/location-plot-plans"), payload)
  return response.data
}

export async function updatePlotPlan(plotPlanId: string, payload: PlotPlanUpsertRequest): Promise<PlotPlanDto> {
  const response = await axios.post<PlotPlanDto>(buildUrl(`/api/v1/location-plot-plans/${plotPlanId}`), payload)
  return response.data
}

export async function uploadPlotPlanVersion({
  tenantId,
  plotPlanId,
  versionName,
  remark,
  file,
}: PlotPlanVersionUploadRequest): Promise<void> {
  const formData = new FormData()
  formData.append("tenantId", tenantId)
  formData.append("plotPlanId", plotPlanId)
  if (versionName) {
    formData.append("versionName", versionName)
  }
  if (remark) {
    formData.append("remark", remark)
  }
  formData.append("file", file)

  await axios.post(buildUrl(`/api/v1/location-plot-plans/${plotPlanId}/versions`), formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

export async function fetchPlotPlanVersions(
  tenantId: string,
  plotPlanId: string,
): Promise<PlotPlanVersionDto[]> {
  const response = await axios.get<PlotPlanVersionDto[]>(
    buildUrl(`/api/v1/location-plot-plans/${plotPlanId}/versions`),
    { params: { tenantId } },
  )
  return response.data ?? []
}

export async function fetchWorkAreas(tenantId: string): Promise<WorkAreaOption[]> {
  const response = await axios.get<WorkAreaOption[]>(buildUrl("/api/v1/work-areas"), {
    params: { tenantId },
  })
  return response.data ?? []
}

export async function fetchUnits(locationIdOrCode: string): Promise<UnitOption[]> {
  if (!locationIdOrCode) return []
  const response = await axios.get<UnitOption[]>(buildUrl("/api/v1/work-areas/units"), {
    params: { locationId: locationIdOrCode },
  })
  return response.data ?? []
}
